import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import Seo from "../../components/Seo";
import { Avatar, EmptyState, Modal, PageHeader, Spinner } from "../../components/lawyer/LawyerUi";
import { apiErrorMessage, isBlankFile, userService } from "../../services/userApi";
import { QuestionStatus } from "./UserRequests";

const fileName = (url) => decodeURIComponent((url || "").split("/").pop().split("?")[0]);

const UserQuestionDetails = () => {
  const { questionId } = useParams();
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [closeOpen, setCloseOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [openingChat, setOpeningChat] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    userService
      .getQuestionDetails(questionId)
      .then((res) => {
        if (!active) return;
        if (!res?.data?.status) throw new Error(res?.data?.message);
        setQuestion(res.data.data);
      })
      .catch((err) => active && setError(apiErrorMessage(err, err?.message || "Unable to load this question.")))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [questionId]);

  const closeQuestion = async () => {
    setClosing(true);
    try {
      const res = await userService.closeQuestion(question.id);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      setQuestion((q) => ({ ...q, status: "Closed" }));
      toast.success(res.data.message || "Question closed");
      setCloseOpen(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to close the question."));
    } finally {
      setClosing(false);
    }
  };

  const openChat = async (answer) => {
    if (!answer.lawyer?.id) return;
    setOpeningChat(answer.id);
    try {
      const res = await userService.openLawyerChat(answer.lawyer.id);
      const chatId = res?.data?.data?.chat?.id;
      if (!chatId) throw new Error(res?.data?.message);
      navigate(`/user/chat/${chatId}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to open the chat."));
    } finally {
      setOpeningChat(null);
    }
  };

  const isClosed = String(question?.status || "").toLowerCase() === "closed";
  const attachments = (question?.attachments || []).filter((u) => !isBlankFile(u));
  const answers = question?.answers || [];

  return (
    <div className="lwp-page">
      <Seo title="Question | Legal Platform" noindex />
      <PageHeader
        title="Question Details"
        backTo="/user/requests"
        actions={
          question &&
          !isClosed && (
            <button type="button" className="lwp-pill-btn" onClick={() => setCloseOpen(true)}>
              <i className="bi bi-x-circle" />
              <span>Mark Close</span>
            </button>
          )
        }
      />

      {loading ? (
        <Spinner />
      ) : error || !question ? (
        <EmptyState icon="bi-question-circle" text={error || "Question not found."} />
      ) : (
        <>
          <section className="usp-vc-card">
            <div className="usp-question-head">
              <span className="lwp-meta">
                QQ#{question.id} · {moment(question.created_at).isValid() ? moment(question.created_at).format("D MMM YYYY, hh:mm A") : ""}
              </span>
              <QuestionStatus q={question} />
            </div>
            <p className="usp-question-text">{question.question}</p>
            {question.jurisdiction?.name && (
              <p className="lwp-meta">
                <i className="bi bi-geo-alt" /> {question.jurisdiction.name}
              </p>
            )}
            {attachments.length > 0 && (
              <div className="usp-attachments">
                {attachments.map((url) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer" className="lwp-msg-doc">
                    <i className="bi bi-paperclip" />
                    <span>{fileName(url)}</span>
                  </a>
                ))}
              </div>
            )}
          </section>

          <h3 className="usp-section-title">
            Answers <small>({answers.length})</small>
          </h3>
          {answers.length === 0 ? (
            <EmptyState icon="bi-hourglass-split" text="Lawyers are reviewing your question. You'll be notified when someone answers." />
          ) : (
            <ul className="usp-answers">
              {answers.map((a) => (
                <li key={a.id} className="usp-vc-card">
                  <div className="usp-answer-head">
                    <Link to={a.lawyer ? `/user/lawyers/${a.lawyer.id}` : "#"} className="usp-answer-lawyer">
                      <Avatar src={a.lawyer?.picture} alt={a.lawyer?.name} size={44} />
                      <span>
                        <strong>{a.lawyer?.company_name || a.lawyer?.name || "Lawyer"}</strong>
                        <small>{moment(a.created_at).isValid() ? moment(a.created_at).fromNow() : ""}</small>
                      </span>
                    </Link>
                    {a.lawyer?.id && (
                      <button
                        type="button"
                        className="lwp-icon-btn usp-answer-chat"
                        onClick={() => openChat(a)}
                        disabled={openingChat === a.id}
                        aria-label={`Chat with ${a.lawyer.name || "lawyer"}`}
                        title="Chat with lawyer"
                      >
                        {openingChat === a.id ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-chat-dots-fill" />}
                      </button>
                    )}
                  </div>
                  <p className="usp-answer-text">{a.answer}</p>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <Modal
        open={closeOpen}
        onClose={() => !closing && setCloseOpen(false)}
        title="Close Question"
        footer={
          <div className="lwp-modal-actions">
            <button type="button" className="lwp-btn lwp-btn-outline" onClick={() => setCloseOpen(false)} disabled={closing}>
              Cancel
            </button>
            <button type="button" className="lwp-btn" onClick={closeQuestion} disabled={closing}>
              {closing ? <span className="spinner-border spinner-border-sm" /> : "Close"}
            </button>
          </div>
        }
      >
        <p className="lwp-modal-text">Close this question? Lawyers won't be able to post new answers.</p>
      </Modal>
    </div>
  );
};

export default UserQuestionDetails;
